import moment from 'moment-timezone'
import { axiosInstance as axios, axiosGraphQlRequest } from '../../utils/axiosInstances'
import {
  createDeviceQuery,
  deleteDeviceQuery,
  getDeviceQuery,
  getEventDataRecordsByAccountQuery
} from '../modules/queries'
import { createDevice, deleteDevice } from './utils/persistence/mutations'
import { persistenceUrl } from '@/store/common'
import { buildGraphQlPayload } from '@/utils/graphql_helpers'
import { getDevices } from './utils/persistence/queries'
import { subscribeToPlan } from './utils/totogi/mutations'
import { N28_SUBSCRIPTION_URL, N28_SUBSCRIPTIONS_CALLBACK_URL } from '@/utils/constants'

export const initialState = () => {
  return {
    demoDeviceEdrEdges: [],
    devices: [],
    billingEDRs: [],
    accountEDRs: [],
    hasMoreBillingEDRs: false,
    billingEDRsEndCursor: null,
    hasMoreAccountEDRs: false,
    accountEDRsEndCursor: null,
    loadingDevice: false,
    loadingPercent: 0,
    loadingStatus: ''
  }
}

export default {
  namespaced: true,
  state: initialState(),
  getters: {
    zainEDRFromTimestamp () {
      const zainStartDate = moment('2024-03-07T00:00:00+02:00').tz('UTC')
      const oneWeekBack = moment().tz('UTC').hour(0).minute(0).add(-7, 'days')
      return (oneWeekBack.isAfter(zainStartDate) ? oneWeekBack : zainStartDate).toISOString()
    }
  },
  mutations: {
    resetState (state) {
      Object.assign(state, initialState())
    },
    setBillingEDRs (state, edrs) {
      state.billingEDRs = edrs
    },
    setAccountEDRs (state, edrs) {
      state.accountEDRs = edrs
    },
    setLoadingDevice (state, newValue) {
      state.loadingDevice = newValue
    },
    setLoadingPercent (state, newValue) {
      state.loadingPercent = newValue
    },
    setLoadingStatus (state, newValue) {
      state.loadingStatus = newValue
    },
    setDemoAccount (state, account) {
      state.demoAccount = account
    },
    setHasMoreBillingEDRs (state, newValue) {
      state.hasMoreBillingEDRs = newValue
    },
    setHasMoreAccountEDRs (state, newValue) {
      state.hasMoreAccountEDRs = newValue
    },
    setBillingEDRsEndCursor (state, newValue) {
      state.billingEDRsEndCursor = newValue
    },
    setAccountEDRsEndCursor (state, newValue) {
      state.accountEDRsEndCursor = newValue
    },
    setDemoAccountBalance (state, balance) {
      state.demoAccountBalance = balance
    },
    setDemoAccountAllocations (state, allocations) {
      state.demoAccountAllocations = allocations
    },
    setDemoAccountPlans (state, plans) {
      state.demoAccountPlans = plans
    },
    setDemoDevice (state, device) {
      state.demoDevice = device
    },
    setDemoDeviceEdrEdges (state, newValue) {
      state.demoDeviceEdrEdges = newValue
    },
    setDemoDeviceRefreshInterval (state, newValue) {
      state.demoDeviceRefreshInterval = newValue
    },
    setDevices (state, newValue) {
      state.devices = newValue
    },
    setN28NotificationRefreshInterval (state, newValue) {
      state.n28NotificationRefreshInterval = newValue
    }
  },
  actions: {
    async delete (context, { deviceId, accountId }) {
      const result = await axios.post(
        context.rootState.awsRegionInfo.aws_appsync_graphqlEndpoint,
        deleteDeviceQuery(
          deviceId,
          accountId,
          context.rootState.providerId
        ), {
          headers: {
            Authorization: `${context.rootState.idToken}`
          }
        }
      )
      if (!result?.data?.data?.deleteDevice?.deletedDeviceId) {
        return
      }
      await axios.post(
        persistenceUrl,
        buildGraphQlPayload(deleteDevice, {
          input: {
            accountId,
            deviceId,
            providerId: context.rootState.providerId
          }
        }),
        context.rootGetters.persistenceGraphQLHeaders
      )
    },
    async create (context, { deviceId, accountId }) {
      const result = await axios.post(
        context.rootState.awsRegionInfo.aws_appsync_graphqlEndpoint,
        createDeviceQuery(
          deviceId,
          accountId,
          context.rootState.providerId
        ),
        {
          headers: {
            Authorization: `${context.rootState.idToken}`
          }
        }
      )
      if (!(['CreateDevicePayload', 'DeviceAlreadyExists'].indexOf(result?.data?.data?.createDevice?.__typename) > -1)) {
        return
      }
      await axios.post(
        persistenceUrl,
        buildGraphQlPayload(createDevice, {
          input: {
            accountId,
            deviceId,
            providerId: context.rootState.providerId
          }
        }),
        context.rootGetters.persistenceGraphQLHeaders
      )
    },
    async getDevice (context, { deviceId }) {
      const result = (await axios.post(
        context.rootState.awsRegionInfo.aws_appsync_graphqlEndpoint,
        getDeviceQuery(
          deviceId,
          context.rootState.providerId
        ),
        context.rootGetters.graphQLHeaders
      )).data.data.getDevice
      if (result.errorCode) {
        throw result
      }
      return result
    },
    async getDeviceDetails (context, { deviceId }) {
      console.log(`Loading demo device ${deviceId}`)
      context.commit('setLoadingDevice', true)
      context.commit('setLoadingPercent', 0)
      context.commit('setLoadingStatus', 'Loading Device Info')
      if (!context.rootState.balanceTypes?.length) {
        await context.dispatch('myProviderConfig/getMyProviderConfig', null, { root: true })
      }
      try {
        context.commit('setDemoDevice', await context.dispatch('getDevice', { deviceId }))
      } catch (err) {
        if (err.errorCode) {
          throw err
        }
      }
      if (!context.rootState.settings.settingsLoaded) {
        await context.dispatch('settings/loadSettings', null, { root: true })
      }
      const device = context.state.demoDevice
      context.commit('setDemoAccount', device.account)
      /* const accountBalance = await context.dispatch('getAccount', { accountId: device.account.id }, { root: true })
      accountBalance.serviceBalances = await buildReadableAllocationsOrBalances(accountBalance.serviceBalances, 'balance')
      context.commit('setDemoAccountBalance', accountBalance)
      if (!context.rootState.allAvailablePlanInformation || !context.rootState.allAvailablePlanInformation.length) {
        await context.dispatch('planVersions/getPlanInformation', null, { root: true })
      } */
      const allPlans = context.rootState.allAvailablePlanInformation || []
      context.commit('setLoadingDevice', false)
      context.commit('setBillingEDRs', [])
      context.commit('setHasMoreBillingEDRs', false)
      context.commit('setBillingEDRsEndCursor', null)
      context.commit('setAccountEDRs', [])
      context.commit('setHasMoreAccountEDRs', false)
      context.commit('setAccountEDRsEndCursor', null)
      context.commit('setLoadingPercent', !allPlans.length ? 25 : 33)
      context.commit('setLoadingStatus', 'Loading Billing EDRs')
      await context.dispatch('loadBillingEDRs')
      context.commit('setLoadingPercent', !allPlans.length ? 50 : 67)
      context.commit('setLoadingStatus', 'Loading Account EDRs')
      await context.dispatch('loadAccountEDRs')
      if (!allPlans.length) {
        context.commit('setLoadingPercent', 75)
        context.commit('setLoadingStatus', 'Loading Plans')
        await context.dispatch('planVersions/getPlanInformation', {}, { root: true })
      }
      context.commit('setLoadingPercent', 0)
      context.commit('setLoadingStatus', '')
      return device
    },
    async loadBillingEDRs (context) {
      if (context.state.loadingDevice) {
        return
      }
      const result = (await axios.post(
        context.rootState.awsRegionInfo.aws_appsync_graphqlEndpoint,
        getEventDataRecordsByAccountQuery(
          context.rootState.providerId,
          context.state.demoDevice.account.id,
          'BILLING',
          context.state.billingEDRsEndCursor,
          context.getters.zainEDRFromTimestamp
        ),
        context.rootGetters.graphQLHeaders
      )).data.data.getEventDataRecordsByAccount
      if (context.state.loadingDevice) {
        return
      }
      const billingEDRs = result.edges.map(edge => {
        edge.node.eventData = JSON.parse(edge.node.eventData)
        edge.node.customData = JSON.parse(edge.node.customData)
        return edge.node
      })
      console.log('Billing EDRs', billingEDRs)
      const pageInfo = result.pageInfo
      context.commit('setBillingEDRs', [...context.state.billingEDRs, ...billingEDRs])
      context.commit('setHasMoreAccountEDRs', pageInfo.hasNextPage)
      context.commit('setBillingEDRsEndCursor', pageInfo.endCursor)
      if (context.state.loadingDevice) {
        return
      }
      console.log('Billing EDRs', pageInfo.hasNextPage, pageInfo.endCursor)
      if (context.state.hasMoreBillingEDRs || billingEDRs.length >= 25) {
        context.dispatch('loadBillingEDRs')
      }
    },
    async loadAccountEDRs (context) {
      if (context.state.loadingDevice) {
        return
      }
      const result = (await axios.post(
        context.rootState.awsRegionInfo.aws_appsync_graphqlEndpoint,
        getEventDataRecordsByAccountQuery(
          context.rootState.providerId,
          context.state.demoDevice.account.id,
          'ACCOUNT',
          context.state.accountEDRsEndCursor,
          context.getters.zainEDRFromTimestamp
        ),
        context.rootGetters.graphQLHeaders
      )).data.data.getEventDataRecordsByAccount
      if (context.state.loadingDevice) {
        return
      }
      const accountEDRs = result.edges.map(edge => {
        edge.node.eventData = JSON.parse(edge.node.eventData)
        edge.node.customData = JSON.parse(edge.node.customData)
        return edge.node
      })
      const pageInfo = result.pageInfo
      context.commit('setAccountEDRs', [...context.state.accountEDRs, ...accountEDRs])
      context.commit('setHasMoreBillingEDRs', pageInfo.hasNextPage)
      context.commit('setAccountEDRsEndCursor', pageInfo.endCursor)
      if (context.state.loadingDevice) {
        return
      }
      if (context.state.hasMoreAccountEDRs || accountEDRs.length >= 25) {
        context.dispatch('loadAccountEDRs')
      }
    },
    async getDemoDevice (context, { deviceId }) {
      console.log(`Loading demo device ${deviceId}`)
      try {
        context.commit('setDemoDevice', await context.dispatch('getDevice', { deviceId }))
      } catch (err) {
        if (err.errorCode) {
          throw err
        }
      }
      if (!context.rootState.settings.settingsLoaded) {
        await context.dispatch('settings/loadSettings', null, { root: true })
      }
      const device = context.state.demoDevice
      context.commit('setDemoAccount', device.account)
      // const accountBalance = await context.dispatch('getAccount', { accountId: device.account.id }, { root: true })
      // accountBalance.serviceBalances = await buildReadableAllocationsOrBalances(accountBalance.serviceBalances, 'balance')
      // context.commit('setDemoAccountBalance', accountBalance)
      if (!context.rootState.allAvailablePlanInformation || !context.rootState.allAvailablePlanInformation.length) {
        await context.dispatch('planVersions/getPlanInformation', null, { root: true })
      }
      await context.dispatch('loadDemoDevicePlans')
      await context.dispatch('resetEdrs', null, { root: true })
      context.dispatch('refreshDemoDeviceBalanceAndInfo')
      return device
    },
    async loadDemoDevicePlans (context, options) {
      const payload = { accountId: context.state.demoAccount.id, includeAllocations: true }
      if (!options || !options.refresh) {
        payload.account = context.state.demoAccount
        console.log('No refresh')
      } else {
        console.log('Refresh')
      }
      console.log('Loading demo device plans')
      const { accountPlans, allocations } = await context.dispatch('account/retrieveAccountPlansAndInfo', payload, { root: true })
      context.commit('setDemoAccountPlans', accountPlans)
      context.commit('setDemoAccountAllocations', allocations)
      console.log('Demo Device Active Plans')
      console.table(accountPlans)
      console.log('Demo Device Allocations')
      /*
        Example allocations
        {
          id: "data.1GB.7e884ec2-c607-401d-9a26-08564268b303",
          name: "Data",
          planVersionId: "85fe518d-0b6b-440a-845e-880a9e33f39f",
          readableValue: "1024 MB",
          subscriptionStartedAt: Moment,
          type: "Data",
          uuid: "7e884ec2-c607-401d-9a26-08564268b303",
          value: 1073741824
        }
        {
          id: "voice-all-net.86acc42b-981d-4985-87f5-4f93eae5be91",
          name: "Voice All Net",
          planVersionId: "bcc2559b-eea6-4528-9aea-83e4af9adc5b",
          readableValue: "Unlimited",
          subscriptionStartedAt: Moment,
          type: "Voice",
          uuid: "86acc42b-981d-4985-87f5-4f93eae5be91",
          value: null
        }
      */
      console.table(allocations)
    },
    async resubscribePlan (context, { planSubscriptionId, planVersionId, performRefresh = true }) {
      console.log('Resubscribing to plan')
      await context.dispatch('clearDemoDeviceRefresh')
      await context.dispatch('clearN28NotificationSubscription')
      await context.dispatch('loadDemoDevicePlans', { refresh: true })
      console.log(`Cancelling plan ${planSubscriptionId} - ${planVersionId}`)
      await context.dispatch('account/invokeCancelPlanSubscription', {
        accountId: context.state.demoAccount.id,
        planSubscriptionId
      }, { root: true })
      await new Promise(resolve => setTimeout(resolve, 1000))
      const result = await axiosGraphQlRequest(
        context.rootState.awsRegionInfo.aws_appsync_graphqlEndpoint,
        subscribeToPlan,
        {
          input: {
            accountId: context.state.demoAccount.id,
            providerId: context.rootState.providerId,
            planVersionId: planVersionId
          }
        },
        context.rootGetters.graphQLHeaders
      )
      const subscribeResult = result?.data?.data?.subscribeToPlan
      if (subscribeResult?.__typename !== 'SubscribeToPlanVersionPayload') {
        throw new Error(subscribeResult?.errorMessage || 'Failed to subscribe to plan')
      }
      await context.dispatch('loadDemoDevicePlans', { refresh: true })
      console.log('Resubscribing to plan complete')
      if (performRefresh) {
        context.dispatch('refreshDemoDeviceBalanceAndInfo')
        await context.dispatch('initiateDemoDeviceRefresh')
      }
    },
    async cancelPlanSubscription (context, { planSubscriptionId, planVersionId, performRefresh = true }) {
      console.log('Cancelling plan susbcription')
      await context.dispatch('clearDemoDeviceRefresh')
      await context.dispatch('clearN28NotificationSubscription')
      await context.dispatch('loadDemoDevicePlans', { refresh: true })
      console.log(`Cancelling plan ${planSubscriptionId} - ${planVersionId}`)
      await context.dispatch('account/invokeCancelPlanSubscription', {
        accountId: context.state.demoAccount.id,
        planSubscriptionId
      }, { root: true })
      if (performRefresh) {
        context.dispatch('refreshDemoDeviceBalanceAndInfo')
        await context.dispatch('initiateDemoDeviceRefresh')
      }
    },
    async provisionNewPlanDemoDevice (context) {
      console.log('Provisoning new demo device')
      await context.dispatch('clearDemoDeviceRefresh')
      await context.dispatch('clearN28NotificationSubscription')
      await context.dispatch('loadDemoDevicePlans', { refresh: true })
      const latestPlanSubscription = await context.dispatch('account/latestPlanSubscription', { activePlans: context.state.demoAccountPlans }, { root: true })
      console.log('Latest susbcription', latestPlanSubscription)
      if (latestPlanSubscription) {
        console.log('Cancelling plan')
        console.log(context.state.demoAccount, context.state.demoAccount.id, latestPlanSubscription.planVersionId, latestPlanSubscription, context.state.demoAccountPlans)
        await context.dispatch('account/cancelPlanSubscription', {
          accountId: context.state.demoAccount.id,
          planVersionId: latestPlanSubscription.planVersionId,
          activePlans: context.state.demoAccountPlans
        }, { root: true })
        await new Promise(resolve => setTimeout(resolve, 1000))
      }
      await context.dispatch('account/subscribeToCurrentPlanVersion', { accountId: context.state.demoAccount.id }, { root: true })
      await context.dispatch('loadDemoDevicePlans', { refresh: true })
      console.log('Provisioning new plan complete', context.state.demoAccountAllocations.map(a => a.subscriptionStartedAt.format()), context.state.demoAccountPlans.map(p => p.planSubscriptionId))
      context.dispatch('refreshDemoDeviceBalanceAndInfo')
      await context.dispatch('initiateDemoDeviceRefresh')
    },
    async refreshDemoDeviceBalanceAndInfo (context) {
      console.log('Refreshing Demo Device information')
      // const accountBalance = await context.dispatch('getAccount', { accountId: context.state.demoDevice.account.id }, { root: true })
      // accountBalance.serviceBalances = await buildReadableAllocationsOrBalances(accountBalance.serviceBalances, 'balance')
      /*
        Example Service balances:
        {
          id: "voice-all-net.86acc42b-981d-4985-87f5-4f93eae5be91",
          name: "Voice All Net",
          readableValue: "0 mins",
          type: "Voice",
          uuid: "86acc42b-981d-4985-87f5-4f93eae5be91",
          value: "0"
        }
        {
          "id": "data.1GB.7e884ec2-c607-401d-9a26-08564268b303",
          "name": "Data",
          "readableValue": "1024 MB",
          "type": "Data",
          "uuid": "7e884ec2-c607-401d-9a26-08564268b303",
          "value": "1073741824"
        }
      */
      /* for (const activePlanVersion of accountBalance.activePlanVersions) {
        for (const planService of activePlanVersion.planVersion.planServices) {
          for (const serviceBalance of accountBalance.serviceBalances) {
            if (planService.balanceName === serviceBalance.id) {
              serviceBalance.planId = activePlanVersion.planVersion.plan.id
              serviceBalance.planName = activePlanVersion.planVersion.plan.name
              serviceBalance.planSubscriptionId = activePlanVersion.planSubscriptionId
              serviceBalance.from = activePlanVersion.from
              serviceBalance.to = activePlanVersion.to
              serviceBalance.planVersionName = activePlanVersion.planVersion.version
              serviceBalance.planVersionId = activePlanVersion.planVersion.id
              break
            }
          }
        }
      } */
      console.log('Account Balances')
      // console.table(accountBalance.serviceBalances.concat([{ name: 'Monetary', value: accountBalance.monetaryBalance }]))
      console.log('Active plan versions')
      // console.table(accountBalance.activePlanVersions)
      // context.commit('setDemoAccountBalance', accountBalance)
      context.dispatch('notifications/update', { accountId: context.state.demoAccount.id, deviceId: context.state.demoDevice.id }, { root: true })
      const mappedEdrEdges = await context.dispatch('getAndSummarizeEdrs', { deviceId: context.state.demoDevice.id }, { root: true })
      context.commit('setDemoDeviceEdrEdges', mappedEdrEdges)
      try {
        const device = await context.dispatch('getDevice', { deviceId: context.state.demoDevice.id })
        context.commit('setDemoAccount', device.account)
      } catch (err) {
        if (err.errorCode) {
          throw err
        }
      }
    },
    async subscribeN28Notifications (context) {
      const result = await axios.post(N28_SUBSCRIPTION_URL, {
        supi: context.state.demoDevice.id,
        notifUri: N28_SUBSCRIPTIONS_CALLBACK_URL,
        expiry: moment().add(10, 'minute').utc().toISOString()
      }, {
        headers: {
          Authorization: `${context.rootState.idToken}|${context.rootState.awsRegionInfo.aws_appsync_region}`
        }
      })
      console.log('N28 subscription complete', result)
    },
    async resetDemoDevice (context) {
      console.log('Resetting Demo Device')
      context.commit('setDemoDevice', null)
      context.commit('setDemoAccount', null)
      context.commit('setDemoAccountBalance', null)
      context.commit('setDemoAccountPlans', null)
    },
    async initiateDemoDeviceRefresh (context) {
      context.dispatch('clearDemoDeviceRefresh')
      context.dispatch('clearN28NotificationSubscription')
      context.commit('setDemoDeviceRefreshInterval', setInterval(() => {
        console.log('Demo device refresh interval')
        context.dispatch('refreshDemoDeviceBalanceAndInfo')
      }, 6000))
      context.dispatch('subscribeN28Notifications')
      context.commit('setN28NotificationRefreshInterval', setInterval(() => {
        console.log('N28 Notification subscription interval')
        context.dispatch('subscribeN28Notifications')
      }, 600000))
    },
    async clearDemoDeviceRefresh (context) {
      if (context.state.demoDeviceRefreshInterval) {
        clearInterval(context.state.demoDeviceRefreshInterval)
        context.commit('setDemoDeviceRefreshInterval', null)
      }
    },
    async clearN28NotificationSubscription (context) {
      if (context.state.n28NotificationRefreshInterval) {
        clearInterval(context.state.n28NotificationRefreshInterval)
        context.commit('setN28NotificationRefreshInterval', null)
      }
    },
    async loadDevices (context) {
      const devices = (await axios.post(
        persistenceUrl,
        buildGraphQlPayload(getDevices, {
          providerId: context.rootState.providerId
        }),
        context.rootGetters.persistenceGraphQLHeaders
      )).data.data.getDevices.devices
      devices.forEach((device, index) => {
        if (!device.deviceName || device.deviceName === '-') {
          const account = (context.rootState.account.accounts || []).filter(account => account.accountId === device.accountId)[0]
          device.deviceName = `Dev-${Number(index) + 1}`
          device.account = account
        }
      })
      context.commit('setDevices', devices.sort((a, b) => a.createdAt > b.createdAt ? 1 : -1))
    }
  }
}
